import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Facebook, Instagram, Twitter, Youtube, ArrowRight, Shield, Truck, RotateCcw, Zap } from "lucide-react";

const SHOP_LINKS = [
  ["Computing",               "/shop?category=Computing"],
  ["Electronics",             "/shop?category=Electronics"],
  ["Power & Solar",           "/shop?category=Power%20%26%20Solar"],
  ["Networking",              "/shop?category=Networking"],
  ["Home & Office",           "/shop?category=Home%20%26%20Office"],
  ["Fashion",                 "/shop?category=Fashion"],
  ["Beauty & Personal Care",  "/shop?category=Beauty%20%26%20Personal%20Care"],
  ["Kids & Toys",             "/shop?category=Kids%20%26%20Toys"],
  ["Party & Gifts",           "/shop?category=Party%20%26%20Gifts"],
  ["Solar Load Calculator",   "/solar-calculator"],
];

const COMPANY_LINKS = [
  ["About Us",        "/about"],
  ["Sell on Afriport","/register"],
  ["Affiliate Program","/register"],
  ["Terms of Service","/terms"],
  ["Privacy Policy",  "/privacy"],
  ["Return Policy",   "/returns"],
];

const TRUST_ITEMS = [
  { icon: Shield,    label: "100% Authentic" },
  { icon: Truck,     label: "Fast Delivery"  },
  { icon: RotateCcw, label: "30-Day Returns" },
  { icon: Zap,       label: "Best Prices"    },
];

const SOCIALS = [
  { icon: Facebook,  href: "https://facebook.com/Afriportstore",  color: "#1877F2" },
  { icon: Instagram, href: "https://instagram.com/Afriportstore",  color: "#E4405F" },
  { icon: Twitter,   href: "https://twitter.com/Afriportstore",    color: "#1DA1F2" },
  { icon: Youtube,   href: "https://youtube.com/@Afriportstore",   color: "#FF0000" },
];

export default function Footer() {
  const [email, setEmail]     = useState("");
  const [subDone, setSubDone] = useState(false);

  function handleSubscribe(e) {
    e.preventDefault();
    if (!email) return;
    setSubDone(true);
    setEmail("");
    setTimeout(() => setSubDone(false), 4000);
  }

  return (
    <footer>
      {/* ── Trust Strip ──────────────────────────────────────────────── */}
      <div style={{ background: "var(--brand)", padding: "18px 0" }}>
        <div className="container">
          <div className="footer-trust-strip">
            {TRUST_ITEMS.map(({ icon: Icon, label }, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, padding: "6px 20px", borderRight: i < 3 ? "1px solid rgba(255,255,255,0.25)" : "none" }}>
                <Icon size={18} color="#fff" />
                <span style={{ fontSize: 13, fontWeight: 700, color: "#fff", letterSpacing: "0.02em" }}>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Newsletter ───────────────────────────────────────────────── */}
      <div style={{ background: "#0D1B2A", borderBottom: "1px solid rgba(255,255,255,0.06)", padding: "48px 0" }}>
        <div className="container">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 32, flexWrap: "wrap" }}>
            <div style={{ maxWidth: 420 }}>
              <h3 style={{ fontFamily: "var(--font-head)", fontSize: 24, fontWeight: 800, color: "#fff", marginBottom: 8 }}>
                Stay in the loop
              </h3>
              <p style={{ color: "#94A3B8", fontSize: 14, lineHeight: 1.6 }}>
                Get notified about new arrivals, exclusive deals and restocks before everyone else.
              </p>
            </div>
            <form onSubmit={handleSubscribe} style={{ display: "flex", gap: 10, flex: "0 0 auto", maxWidth: 420, width: "100%", flexWrap: "wrap" }}>
              {subDone ? (
                <div style={{ flex: 1, padding: "12px 20px", background: "rgba(22,163,74,0.15)", border: "1.5px solid rgba(22,163,74,0.4)", borderRadius: "var(--radius-sm)", color: "#4ADE80", fontWeight: 600, fontSize: 14, display: "flex", alignItems: "center", gap: 8 }}>
                  ✓ You're subscribed!
                </div>
              ) : (
                <>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    style={{ flex: 1, minWidth: 200, padding: "12px 16px", borderRadius: "var(--radius-sm)", border: "1.5px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.06)", color: "#fff", fontSize: 14, fontFamily: "var(--font-body)", outline: "none", transition: "border-color 0.2s" }}
                    onFocus={e => e.target.style.borderColor = "var(--brand)"}
                    onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.12)"}
                  />
                  <button type="submit" className="btn btn-primary" style={{ flexShrink: 0, gap: 8 }}>
                    Subscribe <ArrowRight size={15} />
                  </button>
                </>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* ── Main Footer ──────────────────────────────────────────────── */}
      <div style={{ background: "#0D1B2A", padding: "64px 0 48px" }}>
        <div className="container">
          <div className="footer-main-grid">

            {/* Brand column */}
            <div>
              <Link to="/" style={{ display: "inline-flex", alignItems: "center", marginBottom: 16, textDecoration: "none" }}>
                <img src="/images/Afriportstore.png" alt="Afriport" style={{ height: 48, width: "auto", objectFit: "contain" }} onError={e => { e.currentTarget.style.display = 'none'; }} />
              </Link>
              <p style={{ fontSize: 14, color: "#94A3B8", lineHeight: 1.7, marginBottom: 24, maxWidth: 300 }}>
                Better quality of living for Nigerian families through reliable household essentials, lifestyle items, and energy autonomy.
              </p>

              {/* Social icons */}
              <div style={{ display: "flex", gap: 10, marginBottom: 24 }}>
                {SOCIALS.map(({ icon: Icon, href, color }) => (
                  <a key={href} href={href} target="_blank" rel="noopener noreferrer"
                    style={{ width: 38, height: 38, borderRadius: "var(--radius-sm)", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.08)", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s" }}
                    onMouseEnter={e => { e.currentTarget.style.background = `${color}22`; e.currentTarget.style.borderColor = `${color}50`; e.currentTarget.firstChild.style.color = color; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.06)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)"; e.currentTarget.firstChild.style.color = "#9CA3AF"; }}
                  >
                    <Icon size={16} color="#9CA3AF" style={{ transition: "color 0.2s" }} />
                  </a>
                ))}
              </div>

              {/* Contact */}
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Mail size={14} color="#E8471A" />
                  <a href="mailto:afriportx@gmail.com" style={{ fontSize: 13, color: "#94A3B8", transition: "color 0.2s" }}
                    onMouseEnter={e => e.target.style.color = "#fff"}
                    onMouseLeave={e => e.target.style.color = "#94A3B8"}>afriportx@gmail.com</a>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 14 }}>🌐</span>
                  <a href="https://www.AfriportB2C.online" target="_blank" rel="noreferrer" style={{ fontSize: 13, color: "#94A3B8", transition: "color 0.2s" }}
                    onMouseEnter={e => e.target.style.color = "#fff"}
                    onMouseLeave={e => e.target.style.color = "#94A3B8"}>www.AfriportB2C.online</a>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Phone size={14} color="#E8471A" />
                  <a href="tel:+2349155896484" style={{ fontSize: 13, color: "#94A3B8", transition: "color 0.2s" }}
                    onMouseEnter={e => e.target.style.color = "#fff"}
                    onMouseLeave={e => e.target.style.color = "#94A3B8"}>+234 915 589 6484</a>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <MapPin size={14} color="#E8471A" />
                  <span style={{ fontSize: 13, color: "#94A3B8" }}>Nationwide Delivery, Nigeria</span>
                </div>
              </div>
            </div>

            {/* Shop links */}
            <div>
              <h4 style={{ fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 14, color: "#fff", marginBottom: 20, textTransform: "uppercase", letterSpacing: "0.05em" }}>Shop</h4>
              <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
                {SHOP_LINKS.map(([label, href]) => (
                  <Link key={label} to={href}
                    style={{ fontSize: 14, color: "#64748B", transition: "color 0.15s" }}
                    onMouseEnter={e => e.target.style.color = "#fff"}
                    onMouseLeave={e => e.target.style.color = "#64748B"}>
                    {label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Company links */}
            <div>
              <h4 style={{ fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 14, color: "#fff", marginBottom: 20, textTransform: "uppercase", letterSpacing: "0.05em" }}>Company</h4>
              <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
                {COMPANY_LINKS.map(([label, href]) => (
                  <Link key={label} to={href}
                    style={{ fontSize: 14, color: "#64748B", transition: "color 0.15s" }}
                    onMouseEnter={e => e.target.style.color = "#fff"}
                    onMouseLeave={e => e.target.style.color = "#64748B"}>
                    {label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Working hours & payment */}
            <div>
              <h4 style={{ fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 14, color: "#fff", marginBottom: 20, textTransform: "uppercase", letterSpacing: "0.05em" }}>Business Hours</h4>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 28 }}>
                {[
                  ["Mon – Fri:", "8:00 AM – 6:00 PM"],
                  ["Saturday:", "9:00 AM – 4:00 PM"],
                  ["Sunday:",   "Closed"],
                ].map(([day, time]) => (
                  <div key={day} style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                    <span style={{ color: "#64748B" }}>{day}</span>
                    <span style={{ color: "#94A3B8", fontWeight: 500 }}>{time}</span>
                  </div>
                ))}
              </div>

              <h4 style={{ fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 14, color: "#fff", marginBottom: 14, textTransform: "uppercase", letterSpacing: "0.05em" }}>We Accept</h4>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {["Paystack", "Bank Transfer", "USSD", "Card"].map(p => (
                  <span key={p} style={{ fontSize: 11, fontWeight: 600, padding: "5px 12px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "var(--radius-sm)", color: "#94A3B8", letterSpacing: "0.03em" }}>{p}</span>
                ))}
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 28, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
            <p style={{ fontSize: 13, color: "#94A3B8" }}>
              &copy; 2026 Afriport Store. All rights reserved.
            </p>
            <div style={{ display: "flex", gap: 20 }}>
              {[["Privacy", "/privacy"], ["Terms", "/terms"], ["Returns", "/returns"]].map(([l, h]) => (
                <Link key={l} to={h} style={{ fontSize: 13, color: "#4B5563", transition: "color 0.15s" }}
                  onMouseEnter={e => e.target.style.color = "#fff"}
                  onMouseLeave={e => e.target.style.color = "#4B5563"}>{l}</Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
